import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Menu, LogOut, User, MessageCirclePlus } from 'lucide-react';
import { auth } from '../../config/firebaseConfig';
import CoordinationTab from '../tabs/CoordinationTab';
import BottomNavigation from './BottomNavigation';
import { useToast } from '../../contexts/ToastContext';
import './styles/MobileGroupsView.css';
import { API_BASE_URL } from '../../config/apiConfig';

const API_BASE = API_BASE_URL;

const MobileGroupsView = ({ user, isVoiceEnabled, setIsVoiceEnabled }) => {
   const navigate = useNavigate();
   const { showToast } = useToast();
   const location = useLocation();
   const [exchangeRequestCount, setExchangeRequestCount] = useState(0);
   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
   const [isInRoom, setIsInRoom] = useState(false);
   const [effectiveIsInRoom, setEffectiveIsInRoom] = useState(false);
   const [currentRoomMode, setCurrentRoomMode] = useState(null);
   const [refreshKey, setRefreshKey] = useState(0);

   const handleRefresh = useCallback(async () => {
      window.dispatchEvent(new Event('refreshRoomList'));
      showToast('새로고침 완료');
   }, [showToast]);

   // 소켓: room-updated → 방 목록 실시간 갱신
   useEffect(() => {
      const userId = user?._id || user?.id;
      if (!userId) return;
      const socket = io(API_BASE, { transports: ['websocket'] }); // Bug 4 수정: reconnectionAttempts 제한 제거
      socket.on('connect', () => socket.emit('join-user', userId));
      socket.on('room-updated', () => window.dispatchEvent(new Event('refreshRoomList')));
      return () => socket.disconnect();
   }, [user]);

   // 방 상태 추적 및 초기화
   useEffect(() => {
      // 방 진입 중(#coordination-room)일 때는 초기화 건너뜀
      // handleRoomClick이 pushState('#coordination-room')을 호출하면 location.key가 바뀌어
      // 이 effect가 재실행되는데, 그 시점에 currentRoomId를 지우면 배지 누적 버그 재발
      if (window.location.hash === '#coordination-room') return;

      // 컴포넌트 마운트 시 무조건 방 목록으로 초기화
      localStorage.removeItem('currentRoomId');
      localStorage.removeItem('currentRoomData');
      window.dispatchEvent(new CustomEvent('clearCurrentRoom'));

      // URL 파라미터로 특정 방 열기 (push 알림 클릭 → SW navigate()로 이동)
      const params = new URLSearchParams(window.location.search);
      const openRoomId = params.get('openRoom') || localStorage.getItem('pendingOpenRoom');
      if (openRoomId) {
         // pendingOpenRoom은 CoordinationTab 마운트 시에도 직접 확인하므로 중복 처리될 수 있음
         // → 이미 CoordinationTab 마운트 effect가 처리했을 수 있으므로 localStorage 확인 후 발송
         if (localStorage.getItem('pendingOpenRoom')) localStorage.removeItem('pendingOpenRoom');
         window.history.replaceState({}, '', '/groups');
         // 즉시 발송: CoordinationTab의 React effects는 이미 실행됨 (child effects는 parent before)
         window.dispatchEvent(new CustomEvent('openSpecificRoom', { detail: { roomId: openRoomId } }));
      }

      const handleRestore = () => {
         setIsInRoom(true);
         setEffectiveIsInRoom(true);
      };
      const handleClear = () => {
         setIsInRoom(false);
         setEffectiveIsInRoom(false);
      };

      window.addEventListener('restoreRoom', handleRestore);
      window.addEventListener('clearCurrentRoom', handleClear);
      return () => {
         window.removeEventListener('restoreRoom', handleRestore);
         window.removeEventListener('clearCurrentRoom', handleClear);
      };
   }, [location.key]);

   // swNavigateToRoom: 방 안/밖 무관하게 항상 수신 (location.key effect는 방 안일 때 조기 반환하므로 분리)
   useEffect(() => {
      const handleSwNav = (e) => {
         const roomId = e.detail?.roomId;
         if (roomId) {
            localStorage.removeItem('pendingOpenRoom');
            window.dispatchEvent(new CustomEvent('openSpecificRoom', { detail: { roomId } }));
         }
      };
      window.addEventListener('swNavigateToRoom', handleSwNav);
      return () => window.removeEventListener('swNavigateToRoom', handleSwNav);
   }, []);

   // isInRoom 상태가 변경될 때마다 effectiveIsInRoom 동기화
   useEffect(() => {
      setEffectiveIsInRoom(isInRoom || !!localStorage.getItem('currentRoomId'));
   }, [isInRoom]);

   const handleLogout = async () => {
      try {
         await auth.signOut();
         localStorage.removeItem('loginMethod');
         navigate('/auth');
      } catch (error) {
         console.error('Logout error:', error);
      }
   };

   return (
      <div className="mobile-groups-view">
         {/* 사이드바 오버레이 */}
         {isSidebarOpen && (
            <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>
         )}

         {/* 사이드바 */}
         <nav className={`mobile-sidebar ${isSidebarOpen ? 'open' : ''}`}>
            <div className="sidebar-header">
               <h2 className="sidebar-title">메뉴</h2>
               <button className="sidebar-close-btn" onClick={() => setIsSidebarOpen(false)}>✕</button>
            </div>
            <div className="sidebar-menu">
               <button className="sidebar-item" onClick={() => navigate('/')}>
                  🏠 홈으로
               </button>
               <button className="sidebar-item" onClick={() => navigate('/schedule')}>
                  📅 내 일정
               </button>
               <button className="sidebar-item" onClick={() => navigate('/groups')}>
                  👥 그룹
               </button>
               <button className="sidebar-item" onClick={() => navigate('/calendar')}>
                  📆 달력
               </button>
               <button className="sidebar-item" onClick={() => navigate('/settings')}>
                  ⚙️ 설정
               </button>
            </div>
         </nav>

         {/* 모바일 헤더 (대화형 방 진입 시 숨김) */}
         {currentRoomMode !== 'conversational' && <header className="mobile-header">
            <div className="mobile-header-content">
               {/* 왼쪽: 햄버거 메뉴 + 로고 */}
               <div className="mobile-header-left">
                  <button className="mobile-menu-btn" onClick={() => setIsSidebarOpen(true)}>
                     <Menu size={24} />
                  </button>
                  <div className="mobile-logo-btn" onClick={() => navigate('/')}>
                     <div className="mobile-logo-wrapper">
                        <img src="/heyheylogo.png" alt="MeetAgent Logo" className="mobile-logo-img" />
                        <div className={`mobile-login-indicator ${user?.google?.refreshToken ? 'google' : 'local'}`}></div>
                     </div>
                     <h1 className="mobile-logo-text">MeetAgent</h1>
                  </div>
               </div>

               {/* 오른쪽: 버튼들 */}
               <div className="mobile-header-right">
                  {/* 프로필 버튼 */}
                  <button className="mobile-profile-btn" onClick={() => navigate('/settings')} title="설정">
                     {user && user.firstName ? user.firstName : <User size={18} />}
                  </button>

                  {/* 로그아웃 버튼 */}
                  <button
                     className="mobile-logout-btn"
                     onClick={handleLogout}
                     title="로그아웃">
                     <LogOut size={16} />
                  </button>
               </div>
            </div>
         </header>}

         {/* 페이지 제목 (방 목록일 때만 표시) */}
         {!effectiveIsInRoom && (
            <div className="groups-page-title">
               <div className="title-with-badge">
                  <h2>그룹</h2>
                  {exchangeRequestCount > 0 && (
                     <span className="notification-badge">{exchangeRequestCount}</span>
                  )}
               </div>

               <div className="group-action-buttons" style={{ display: 'flex', gap: '8px' }}>
                  {/* 방 생성 버튼 */}
                  <button
                     className="group-action-btn create-btn"
                     onClick={() => window.dispatchEvent(new CustomEvent('openCreateRoom'))}
                     title="그룹 만들기"
                  >
                     <MessageCirclePlus size={22} />
                  </button>
               </div>
            </div>
         )}
         
         {/* 그룹 컨텐츠 */}
         <div className={`groups-content ${!effectiveIsInRoom ? 'with-bottom-nav' : 'in-room'}`}>
            <CoordinationTab
               key={`${refreshKey}`}
               user={user} 
               onExchangeRequestCountChange={setExchangeRequestCount}
               hideHeader={true}
               initialClear={true}
               isMobile={true}
               onRoomStatusChange={(inRoom, mode) => { setIsInRoom(inRoom); setCurrentRoomMode(inRoom ? mode : null); }}
            />
         </div>

         {/* 하단 네비게이션 바 (방 목록에 있을 때만 표시) */}
         {!effectiveIsInRoom && (
            <BottomNavigation
               onRefresh={handleRefresh}
            />
         )}

      </div>
   );
};

export default MobileGroupsView;